import React, { useContext, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import { getAppConfig } from "@config/appConfig";
import { PLACEHOLDER_MOVIE_POSTER } from "@config/ImageSizes";
import { AuthContext } from "@context/AuthContext";
import { PromoContext } from "@context/PromoContext";
import { SearchContext } from "@context/SearchContext";
import Loader from "@components/Ui/Loader";
import DateSelector from "@components/V3/DateSelector";
import FilteredExperience from "@components/V3/Experiences/filteredExperience";
import SelectExperienceSessions from "@components/V3/Experiences/selectExperience";
import { requestUserLocation } from "@utils/locationUtils";
import { groupSessionsByVenueWithMovies } from "@utils/sessionGroupingUtils";
import { getUniqueSessionDates } from "@utils/sessionUtils";
import { getAllExperiences } from "@utils/ticketUtils";
import "./style.scss";

const Experiences = ({ defaultSearchDistance = 15 }) => {
    const location = useLocation();
    const { user, userCoords, setUserCoords } = useContext(AuthContext);
    const { sessions, fetchMovieSessions, isLoadingSessions } = useContext(SearchContext);
    const { fetchApplyPromoAvailability } = useContext(PromoContext);

    const config = getAppConfig();
    const fallbackImage = `${config?.cdnUrl}${PLACEHOLDER_MOVIE_POSTER}`;

    const [selectedDate, setSelectedDate] = useState("");
    const [hasGroupedOnce, setHasGroupedOnce] = useState(false);

    const currentPath = location.pathname;
    const isMainExperiencesPage = currentPath === "/experiences";
    const slug = !isMainExperiencesPage ? currentPath.split("/")[2]?.toLowerCase() : null;
    const allExperiences = getAllExperiences();
    const experienceMatch = slug ? allExperiences.find((exp) => exp.label.toLowerCase() === slug) : null;
    const bannerImage = experienceMatch?.banner || "experiences-banner.jpg";
    const experienceLabel = experienceMatch?.label || "";
    const experienceCode = experienceMatch?.code || null;
    const uniqueDates = useMemo(() => getUniqueSessionDates(sessions), [sessions]);
    const ticketClassId = experienceMatch?.id;

    const effectiveDate = selectedDate || uniqueDates[0] || "";

    const filteredSessions = effectiveDate
        ? sessions.filter(s => s.session_date?.slice(0, 10) === effectiveDate)
        : sessions;

    const groupedVenues = filteredSessions.reduce(groupSessionsByVenueWithMovies, []);

    const isGrouping = isLoadingSessions || (!!ticketClassId && !hasGroupedOnce);

    useEffect(() => {
        if (!userCoords?.latitude || !userCoords?.longitude) {
            requestUserLocation((coords) => {
                if (coords) {
                    setUserCoords(coords);
                } else {
                    setHasGroupedOnce(true);
                }

                if (!ticketClassId) setHasGroupedOnce(true);
            });
        } else if (!ticketClassId) setHasGroupedOnce(true);
    }, [userCoords]);

    useEffect(() => {
        if (user?.id) {
            fetchApplyPromoAvailability();
        }
    }, [user?.id]);

    useEffect(() => {
        if (!userCoords?.latitude || !userCoords?.longitude || !ticketClassId) return;

        setHasGroupedOnce(false);
        const result = fetchMovieSessions({
            ticketClassId,
            search_distance: defaultSearchDistance,
            lat: userCoords.latitude,
            lng: userCoords.longitude,
        });

        const settle = () => setHasGroupedOnce(true);
        if (result && typeof result.finally === "function") {
            result.finally(settle);
        } else if (result && typeof result.then === "function") {
            result.then(settle, settle);
        } else {
            settle();
        }
    }, [userCoords?.latitude, userCoords?.longitude, ticketClassId]);

    const handleDateSelect = (date) => {
        setSelectedDate(date);
    };

    return (
        <div className="experiences">
            <div className="experiences__wrapper">
                <picture className="experiences__picture">
                    <source
                        media="(min-width: 768px)"
                        srcSet={`${config?.cdnUrl}asset/img/${bannerImage}`}
                    />
                    <img
                        className="experiences__image"
                        src={`${config?.cdnUrl}asset/img/${bannerImage}`}
                        alt="Experiences Image"
                    />
                </picture>
                {!isMainExperiencesPage && (
                    <>
                        <div className="experiences__text-container">
                            <h1 className="experiences__title">Experiences</h1>
                            <p className="experiences__text">{experienceLabel}</p>
                        </div>
                        <div className={`experiences__triangle experiences__triangle--${experienceCode}`} />
                    </>
                )}
            </div>
            {isMainExperiencesPage ? (
                <>
                    <div className="experiences__intro">
                        <h1 className="experiences__main-title">
                            Experiences
                        </h1>
                        <p className="paragraph">
                            Discover the cinema experience that fits your vibe —
                            with our fabulous cinema partners bringing you
                            everything from luxe Gold Class to cosy Standard,
                            and all points in between.
                        </p>
                    </div>
                    <SelectExperienceSessions />
                </>
            ) : (
                <div className="experiences__main-list">
                    <DateSelector
                        uniqueDates={uniqueDates}
                        handleClickDate={handleDateSelect}
                        selectedDate={effectiveDate}
                    />
                    {isGrouping ? (
                        <Loader isLoading text="Fetching Movies" />
                    ) : groupedVenues.length > 0 ? (
                        <FilteredExperience
                            groupedVenues={groupedVenues}
                            selection={{
                                date: effectiveDate,
                                experience: ticketClassId
                            }}
                            onImageError={e => (e.target.src = fallbackImage)}
                        />
                    ) : hasGroupedOnce ? (
                        <div className="experiences__message">
                            <p className="experiences__message-title">
                                Sorry, we couldn&apos;t find any results for {experienceLabel}
                                {effectiveDate && (
                                    <>
                                        {" "}
                                        on{" "}
                                        {format(
                                            new Date(effectiveDate),
                                            "EEE d MMM"
                                        )}
                                    </>
                                )}
                            </p>
                            <p className="experiences__message-text">
                                Please select another experience type
                            </p>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default Experiences;
